import { FC, useEffect } from "react"
import { css, Theme } from "@emotion/react"
import { CustomerCarouselImage, useCustomerCarouselImages } from "@ncs/ncs-api"
import {
	useScreenSizeMatch,
	Box,
	Carousel,
	handleVisualSearch,
	TrackingEvent,
	Icon,
} from "@ncs/web-legos"
import { useHistory } from "react-router-dom"
import { useAnalytics } from "~/hooks/useAnalytics"

const CustomPrevArrow = (onClickHandler: () => void, hasPrev: boolean) => {
	return (
		hasPrev && (
			<button
				css={prevArrowStyle}
				onClick={onClickHandler}
				type="button"
				aria-label="Previous slide"
				data-testid="prev-arrow"
			>
				<Icon icon="chevron-left" color="white" aria-hidden="true" />
			</button>
		)
	)
}

const CustomNextArrow = (onClickHandler: () => void, hasNext: boolean) => {
	return (
		hasNext && (
			<button
				css={nextArrowStyle}
				onClick={onClickHandler}
				type="button"
				aria-label="Next slide"
				data-testid="next-arrow"
			>
				<Icon icon="chevron-right" color="white" aria-hidden="true" />
			</button>
		)
	)
}

export const ShopCarousel: FC = () => {
	const history = useHistory()
	const screenIsTiny = useScreenSizeMatch("xs")
	const screenIsSmall = useScreenSizeMatch("sm")
	const screenIsMd = useScreenSizeMatch("md")
	const screenIsLg = useScreenSizeMatch("lg")
	const screenIsXl = useScreenSizeMatch("xl")
	const swipeToleranceThreshold = 24
	const carouselIntervalMS = 7000
	const { trackEvent } = useAnalytics()

	const [images, imagesLoading] = useCustomerCarouselImages()

	useEffect(() => {
		if (images && images.length > 0) {
			const firstImage = images.sort((a, b) => a.presentationOrder - b.presentationOrder)[0]
			const imageUrl = getCarouselImageUrl(
				firstImage,
				screenIsTiny,
				screenIsSmall,
				screenIsMd,
				screenIsLg,
				screenIsXl
			)

			const link = document.createElement("link")
			link.rel = "preload"
			link.as = "image"
			link.href = imageUrl
			document.head.appendChild(link)
		}
	}, [images, screenIsTiny, screenIsSmall, screenIsMd, screenIsLg, screenIsXl])

	useEffect(() => {
		const carouselWrapper = document.querySelector(".carousel-wrapper")
		if (carouselWrapper) {
			carouselWrapper.classList.add("js-enabled")
		}
	}, [])

	const trackEventForCarouselImage = (imageName: string) => {
		if (!imageName) return
		if (imageName.includes("Chains & Rollers")) {
			trackEvent(TrackingEvent.CHAINS_N_ROLLERS)
		} else if (imageName.includes("Clearance")) {
			trackEvent(TrackingEvent.CAROUSEL_CLEARANCE)
		} else if (imageName.includes("Chemicals")) {
			trackEvent(TrackingEvent.CAROUSEL_CHEMS)
		} else if (imageName.includes("Motors")) {
			trackEvent(TrackingEvent.MOTORS)
		} else if (imageName.includes("OEM")) {
			trackEvent(TrackingEvent.MOTORS)
		}
	}

	const handleClickItem = (index: number) => {
		trackEvent(TrackingEvent.CAROUSEL)
		const destinationUrl = images ? images[index].destinationUrl : undefined
		if (destinationUrl) {
			if (destinationUrl === "visual-search") {
				trackEvent(TrackingEvent.CAROUSEL_VISUAL_SEARCH)
				handleVisualSearch()
			} else if (destinationUrl.startsWith(window.location.origin)) {
				history.push(destinationUrl.replace(window.location.origin, ""))
			} else {
				window.open(destinationUrl, "_blank")
			}
		}

		if (images) {
			trackEventForCarouselImage(images[index].name)
		}
	}

	return (
		<Box css={shopCarouselStyle}>
			<div className="carousel-wrapper">
				{!imagesLoading && images && images?.length > 0 && (
					<Carousel
						interval={carouselIntervalMS}
						autoPlay
						infiniteLoop
						emulateTouch={screenIsSmall}
						swipeable={screenIsSmall}
						onClickItem={handleClickItem}
						height={screenIsSmall ? "164px" : "332px"}
						stopOnHover={false}
						showStatus={false}
						showIndicators={true}
						showThumbs={false}
						renderArrowPrev={CustomPrevArrow}
						renderArrowNext={CustomNextArrow}
						renderIndicator={(onClickHandler, isSelected, index, label) => {
							return (
								<li>
									<button
										type="button"
										onClick={onClickHandler}
										className={isSelected ? "selected" : ""}
										aria-label={`Go to slide ${index + 1} of ${images.length}`}
										aria-hidden={false}
										aria-current={isSelected ? "true" : undefined}
										css={navigationDots(isSelected)}
									/>
								</li>
							)
						}}
					>
						{images
							.sort((a, b) => a.presentationOrder - b.presentationOrder)
							.map((image, idx) => (
								<div key={image.id} css={image.destinationUrl && pointerStyle}>
									<img
										height={screenIsSmall ? "164px" : "332px"}
										width="100%"
										loading={idx === 0 ? "eager" : "lazy"}
										css={imageStyle}
										src={getCarouselImageUrl(
											image,
											screenIsTiny,
											screenIsSmall,
											screenIsMd,
											screenIsLg,
											screenIsXl
										)}
										alt={image.name}
									/>
									{image.text && <p className="legend">{image.text}</p>}
								</div>
							))}
					</Carousel>
				)}
			</div>
		</Box>
	)
}

const imageStyle = (theme: Theme) => css`
	height: auto; // Adjust as necessary
	width: auto;
	vertical-align: top;
	border: 0;
	min-width: 100%;
	max-height: 332px;
	height: 332px;
	object-fit: cover;
	object-position: center;
	border-radius: 8px;

	${theme.breakpoints.down("sm")} {
		height: 164px;
	}
`

const pointerStyle = css`
	cursor: pointer;
`

const nextArrowStyle = css`
	padding-top: 6px;
	background: #111827;
	height: 40px;
	width: 40px;
	border-radius: 10em;
	right: 24px;
	z-index: 1;
	position: absolute;
	top: 45%;
	border: none;
	vertical-align: middle;
	text-align: center;
	opacity: 0.3;

	&:hover {
		opacity: 0.8;
		border: 2px solid white;
	}
`

const prevArrowStyle = css`
	padding-top: 6px;
	text-align: center;
	vertical-align: middle;
	border: none;
	background: #111827;
	height: 40px;
	width: 40px;
	border-radius: 10em;
	left: 24px;
	z-index: 1;
	position: absolute;
	top: 45%;
	opacity: 0.3;

	&:hover {
		opacity: 0.8;
		border: 2px solid white;
	}
`

const shopCarouselStyle = (theme: Theme) => css`
	margin-top: 0px;

	ul.control-dots {
		display: flex;
		justify-content: center;
	}
`

const navigationDots = (isSelected: boolean) => css`
	width: 16px;
	height: 16px;
	border-radius: 50%;
	margin: 0 6px;
	border: none;
	background: ${isSelected ? "#005fa3" : "#ccc"};
	cursor: pointer;

	&:focus {
		outline: 2px solid #005fa3;
		outline-offset: 2px;
	}
`

const getCarouselImageUrl = (
	image: CustomerCarouselImage,
	screenIsTiny: boolean,
	screenIsSmall: boolean,
	screenIsMd: boolean,
	screenIsLg: boolean,
	screenIsXl: boolean
): string => {
	if (image.mobileImageUrl && screenIsTiny) {
		return image.mobileImageUrl
	} else if (image.tabletImageUrl && screenIsSmall) {
		return image.tabletImageUrl
	} else if (image.desktopImageUrl && screenIsMd) {
		return image.desktopImageUrl
	} else if (image.tvImageUrl && (screenIsLg || screenIsXl)) {
		return image.tvImageUrl
	}

	return image.desktopImageUrl
}
